"use client";

import { formatChordDisplay, normalizeChordSymbol } from "@bandwith/chords";
import type { Line, Paragraph, Song } from "chordsheetjs";
import { ChordLyricsPair, Comment, Literal, Tag } from "chordsheetjs";
import type React from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getLineText, lineMatchesAnchors } from "./anchorMatching";
import { playChord, playChordFromDefinition } from "./chordAudio";
import { getParagraphLabel, parseChordProContent } from "./chordpro-parser";
import { mergeChordLyricsPairs } from "./chordpro-utils";
import {
  createAnchorMarkup,
  parseFeedbackAnchors,
} from "./parseFeedbackAnchors";
import { SVGuitarRenderer } from "./SVGuitarRenderer";

/**
 * Feedback item with author info for inline comment display.
 */
export type InlineFeedback = {
  id: string;
  body: string;
  author: {
    name: string | null;
    image: string | null;
  };
};

type Props = {
  content: string;
  className?: string;
  /** Show chord diagrams for {define} and {chord} directives */
  showDiagrams?: boolean;
  /** Show inline chords above lyrics (default: true) */
  showChords?: boolean;
  /** Semitones to transpose chords by (default: 0) */
  transpose?: number;
  /** Text snippets to highlight in the lyrics */
  highlights?: string[];
  /** Feedback items to display inline at matching anchor points */
  inlineFeedback?: InlineFeedback[];
  /** Whether to show inline comments (false for performance/fullscreen view) */
  showInlineComments?: boolean;
  /** Called when user selects text and wants to add a comment at that selection */
  onAddCommentAtSelection?: (anchorMarkup: string) => void;
  /** Called when user taps an inline comment to scroll to the full version */
  onInlineCommentClick?: (feedbackId: string) => void;
};

/**
 * Highlight matching text snippets within lyrics.
 * Returns React nodes with highlights wrapped in <mark> elements.
 *
 * Note: This only highlights when the full highlight text is contained in the
 * lyrics segment. Chord-split words (like "Melis[Dm]matic") won't be highlighted
 * because the segments don't contain the full anchor text. This is intentional
 * to match the inline comment placement behavior.
 */
function highlightText(text: string, highlights: string[]): React.ReactNode[] {
  if (!highlights.length || !text) {
    return [text];
  }

  // Build a map of all match positions
  const matches: Array<{ start: number; end: number }> = [];
  const lowerText = text.toLowerCase();

  for (const highlight of highlights) {
    const lowerHighlight = highlight.toLowerCase();
    let pos = 0;
    while (true) {
      const idx = lowerText.indexOf(lowerHighlight, pos);
      if (idx === -1) break;
      matches.push({ start: idx, end: idx + highlight.length });
      pos = idx + 1;
    }
  }

  if (matches.length === 0) {
    return [text];
  }

  // Sort by start position and merge overlapping ranges (spread to avoid mutation)
  const sortedMatches = [...matches].sort((a, b) => a.start - b.start);
  const merged: Array<{ start: number; end: number }> = [];
  for (const match of sortedMatches) {
    const last = merged[merged.length - 1];
    if (last && match.start <= last.end) {
      last.end = Math.max(last.end, match.end);
    } else {
      merged.push({ ...match });
    }
  }

  // Build result with highlighted spans
  const result: React.ReactNode[] = [];
  let pos = 0;
  for (let i = 0; i < merged.length; i++) {
    const match = merged[i];
    if (!match) continue;

    // Add text before this match
    if (match.start > pos) {
      result.push(text.slice(pos, match.start));
    }

    // Add highlighted text
    result.push(
      <mark
        key={`highlight-${i}`}
        className="rounded-sm bg-amber-warm/15 px-0.5 text-inherit"
      >
        {text.slice(match.start, match.end)}
      </mark>,
    );
    pos = match.end;
  }

  // Add remaining text
  if (pos < text.length) {
    result.push(text.slice(pos));
  }

  return result;
}

type ParsedInlineFeedback = InlineFeedback & {
  anchors: string[];
  displayContent: string;
};

/**
 * Parse feedback items to extract their anchors.
 */
function parseFeedbackItems(
  feedback: InlineFeedback[],
): ParsedInlineFeedback[] {
  return feedback.map((item) => {
    const { anchors, displayContent } = parseFeedbackAnchors(item.body);
    return { ...item, anchors, displayContent };
  });
}

/**
 * Get feedback items that have anchors matching text in a line.
 */
function getFeedbackForLine(
  lineText: string,
  parsedFeedback: ParsedInlineFeedback[],
): ParsedInlineFeedback[] {
  if (!lineText.trim()) return [];
  return parsedFeedback.filter(
    (fb) => fb.anchors.length > 0 && lineMatchesAnchors(lineText, fb.anchors),
  );
}

/**
 * Render a single chord/lyrics pair as React elements
 */
function ChordColumn({
  pair,
  lineHasLyrics,
  onChordClick,
  highlights,
}: {
  pair: ChordLyricsPair;
  lineHasLyrics: boolean;
  onChordClick: (chord: string) => void;
  highlights: string[];
}) {
  const formattedChord = pair.chords ? formatChordDisplay(pair.chords) : "";
  // Don't render whitespace-only lyrics - they cause layout issues with flex-wrap.
  // But if the line has lyrics elsewhere, use a non-breaking space placeholder so
  // the chord stays aligned above the lyrics baseline (not collapsed to 0-height).
  const hasLyricsContent = pair.lyrics?.trim();
  let lyrics: string;
  if (hasLyricsContent) {
    lyrics = pair.lyrics ?? "";
  } else if (lineHasLyrics) {
    lyrics = "\u00A0";
  } else {
    lyrics = "";
  }

  return (
    <div className="column">
      {/* biome-ignore lint/a11y/noStaticElementInteractions: Chord is interactive with proper a11y attributes */}
      <span
        className="chord"
        onClick={
          formattedChord ? () => onChordClick(formattedChord) : undefined
        }
        onKeyDown={
          formattedChord
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  onChordClick(formattedChord);
                }
              }
            : undefined
        }
        role={formattedChord ? "button" : undefined}
        tabIndex={formattedChord ? 0 : undefined}
      >
        {formattedChord}
      </span>
      <span className="lyrics">{highlightText(lyrics, highlights)}</span>
    </div>
  );
}

/**
 * Inline comment component displayed below matching lyrics lines.
 * Tapping scrolls to the full comment in the feedback section.
 */
function InlineComment({
  feedback,
  onClick,
}: {
  feedback: ParsedInlineFeedback;
  onClick?: (feedbackId: string) => void;
}) {
  // Get initials for avatar fallback
  const initials =
    feedback.author.name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?";

  const authorLabel = feedback.author.name || "Anonymous";
  const anchorLabel =
    feedback.anchors.length > 0 ? ` about "${feedback.anchors[0]}"` : "";

  return (
    <button
      type="button"
      onClick={() => onClick?.(feedback.id)}
      className="ml-4 mt-1 flex w-full cursor-pointer items-start gap-2 border-l-2 border-amber-warm/30 pl-3 text-left text-xs transition-colors hover:border-amber-warm/50"
      aria-label={`View comment by ${authorLabel}${anchorLabel}`}
    >
      {/* Avatar - always shown */}
      {feedback.author.image ? (
        // biome-ignore lint/performance/noImgElement: Package is platform-agnostic (not Next.js specific), 16px avatars don't benefit from optimization
        <img
          src={feedback.author.image}
          alt=""
          className="h-4 w-4 shrink-0 rounded-full"
        />
      ) : (
        <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-[10px] text-text-muted">
          {initials}
        </div>
      )}
      <div className="flex min-w-0 flex-wrap items-center gap-1">
        {/* Name - hidden on mobile, shown on larger screens */}
        <span className="hidden font-medium text-text-secondary sm:inline">
          {feedback.author.name || "Anonymous"}
        </span>
        {/* Comment content - anchors are already shown in the lyrics context */}
        <span className="text-text-muted">{feedback.displayContent}</span>
      </div>
    </button>
  );
}

/**
 * Render a single line of the chord sheet
 */
function ChordSheetLine({
  line,
  onChordClick,
  highlights,
  lineFeedback,
  onInlineCommentClick,
}: {
  line: Line;
  onChordClick: (chord: string) => void;
  highlights: string[];
  lineFeedback: ParsedInlineFeedback[];
  onInlineCommentClick?: (feedbackId: string) => void;
}) {
  // Check if this line has any renderable content
  if (!line.hasRenderableItems()) {
    return null;
  }

  const elements: React.ReactNode[] = [];

  // First, collect all ChordLyricsPairs and merge them
  const chordLyricsPairs: ChordLyricsPair[] = [];
  const otherItems: Array<{
    index: number;
    item: Comment | Tag | Literal;
  }> = [];

  for (let i = 0; i < line.items.length; i++) {
    const item = line.items[i];
    if (item instanceof ChordLyricsPair) {
      chordLyricsPairs.push(item);
    } else if (
      item instanceof Comment ||
      item instanceof Tag ||
      item instanceof Literal
    ) {
      otherItems.push({ index: i, item });
    }
  }

  // Merge the chord-lyrics pairs to fix word boundary splits
  const mergedPairs = mergeChordLyricsPairs(chordLyricsPairs);
  const lineHasLyrics = mergedPairs.some((p) => p.lyrics?.trim());

  // Render merged chord-lyrics pairs
  for (let i = 0; i < mergedPairs.length; i++) {
    const pair = mergedPairs[i];
    if (!pair) continue;
    elements.push(
      <ChordColumn
        key={`pair-${i}`}
        pair={pair}
        lineHasLyrics={lineHasLyrics}
        onChordClick={onChordClick}
        highlights={highlights}
      />,
    );
  }

  // Render other items (comments, tags, literals)
  for (const { index, item } of otherItems) {
    if (item instanceof Comment) {
      elements.push(
        <span key={`comment-${index}`} className="comment">
          {item.content}
        </span>,
      );
    } else if (item instanceof Tag) {
      // Renderable tags (like {comment: ...}), but skip section delimiters
      // since we render their labels at the paragraph level
      if (item.isRenderable() && !item.isSectionDelimiter()) {
        elements.push(
          <span key={`tag-${index}`} className="comment">
            {item.value}
          </span>,
        );
      }
    } else if (item instanceof Literal) {
      // Literal content (used in tabs, etc.)
      elements.push(
        <span key={`literal-${index}`} className="literal">
          {item.string}
        </span>,
      );
    }
  }

  if (elements.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="row">{elements}</div>
      {lineFeedback.map((fb) => (
        <InlineComment
          key={fb.id}
          feedback={fb}
          onClick={onInlineCommentClick}
        />
      ))}
    </div>
  );
}

/**
 * Render a paragraph (section) of the chord sheet
 */
function ChordSheetParagraph({
  paragraph,
  onChordClick,
  highlights,
  parsedFeedback,
  onInlineCommentClick,
}: {
  paragraph: Paragraph;
  onChordClick: (chord: string) => void;
  highlights: string[];
  parsedFeedback: ParsedInlineFeedback[];
  onInlineCommentClick?: (feedbackId: string) => void;
}) {
  // Skip empty paragraphs
  if (!paragraph.hasRenderableItems()) {
    return null;
  }

  // Determine section class based on type
  const sectionClass = paragraph.type || "";
  const label = getParagraphLabel(paragraph);

  return (
    <div className={`paragraph ${sectionClass}`.trim()}>
      {/* Section label if present */}
      {label && <div className="label">{label}</div>}
      {paragraph.lines.map((line, index) => {
        const lineText = getLineText(line);
        const lineFeedback = getFeedbackForLine(lineText, parsedFeedback);
        return (
          <ChordSheetLine
            // biome-ignore lint/suspicious/noArrayIndexKey: Lines are static, parsed from content
            key={index}
            line={line}
            onChordClick={onChordClick}
            highlights={highlights}
            lineFeedback={lineFeedback}
            onInlineCommentClick={onInlineCommentClick}
          />
        );
      })}
    </div>
  );
}

/**
 * Custom React renderer for ChordPro parsed songs.
 * Renders directly to React elements instead of using dangerouslySetInnerHTML.
 */
function ChordSheetContent({
  song,
  onChordClick,
  highlights,
  parsedFeedback,
  onInlineCommentClick,
}: {
  song: Song;
  onChordClick: (chord: string) => void;
  highlights: string[];
  parsedFeedback: ParsedInlineFeedback[];
  onInlineCommentClick?: (feedbackId: string) => void;
}) {
  // Use expandedBodyParagraphs to handle {chorus} directive expansion
  const paragraphs = song.expandedBodyParagraphs;

  return (
    <div className="chord-sheet">
      {paragraphs.map((paragraph, index) => (
        <ChordSheetParagraph
          // biome-ignore lint/suspicious/noArrayIndexKey: Paragraphs are static, parsed from content
          key={index}
          paragraph={paragraph}
          onChordClick={onChordClick}
          highlights={highlights}
          parsedFeedback={parsedFeedback}
          onInlineCommentClick={onInlineCommentClick}
        />
      ))}
    </div>
  );
}

/** Selection anchor points, relative to the renderer's container */
type SelectionPosition = {
  x: number;
  y: number;
  right: number;
  centerY: number;
};

/**
 * Floating button for adding comment at selection.
 * Uses onPointerDown instead of onClick so it fires before mobile Safari clears the selection.
 * On mobile (touch devices), positions to the right of selection to avoid iOS native popover.
 */
function SelectionPopover({
  position,
  onAddComment,
  onDismiss,
}: {
  position: SelectionPosition;
  onAddComment: () => void;
  onDismiss: () => void;
}) {
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    // Detect touch device on mount
    setIsTouchDevice("ontouchstart" in window || navigator.maxTouchPoints > 0);
  }, []);

  // Handle Escape key to dismiss
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onDismiss();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onDismiss]);

  const handlePointerDown = (e: React.PointerEvent) => {
    // Prevent default to stop Safari from clearing selection before we can use it
    e.preventDefault();
    onAddComment();
  };

  // On touch devices, position to the right of selection to avoid iOS native popover
  // (which can appear above OR below). On desktop, position above (north).
  const positionStyle = isTouchDevice
    ? { left: position.right + 8, top: position.centerY }
    : { left: position.x, top: position.y - 8 };

  const translateClass = isTouchDevice
    ? "-translate-y-1/2" // Right side: center vertically
    : "-translate-x-1/2 -translate-y-full"; // Above: center horizontally, move up

  const arrowClass = isTouchDevice
    ? "absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-bg-tertiary" // Arrow pointing left
    : "absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-bg-tertiary"; // Arrow pointing down

  return (
    <div
      role="tooltip"
      aria-live="polite"
      className={`absolute z-50 ${translateClass} animate-slide-up`}
      style={positionStyle}
    >
      <button
        type="button"
        onPointerDown={handlePointerDown}
        className="flex touch-manipulation items-center gap-1.5 whitespace-nowrap rounded-md border border-border-accent bg-bg-tertiary px-3 py-2 text-sm font-medium text-text-secondary shadow-lg active:bg-bg-secondary"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        Add Feedback
      </button>
      <div className={arrowClass} />
    </div>
  );
}

export const ChordProRenderer = memo(function ChordProRenderer({
  content,
  className = "",
  showDiagrams = true,
  showChords = true,
  transpose = 0,
  highlights = [],
  inlineFeedback = [],
  showInlineComments = false,
  onAddCommentAtSelection,
  onInlineCommentClick,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useState<{
    text: string;
    position: SelectionPosition;
  } | null>(null);

  const { song, chordDefinitions } = useMemo(() => {
    const parsed = parseChordProContent(content, transpose);
    if (!parsed) {
      return { song: null, chordDefinitions: [] };
    }
    return parsed;
  }, [content, transpose]);

  // Parse feedback items to extract anchors (only when showing inline comments)
  const parsedFeedback = useMemo(() => {
    if (!showInlineComments || inlineFeedback.length === 0) {
      return [];
    }
    return parseFeedbackItems(inlineFeedback);
  }, [showInlineComments, inlineFeedback]);

  // Collect all unique highlights from feedback anchors + explicit highlights
  const allHighlights = useMemo(() => {
    const anchorHighlights = parsedFeedback.flatMap((fb) => fb.anchors);
    return [...new Set([...highlights, ...anchorHighlights])];
  }, [highlights, parsedFeedback]);

  // Handle chord clicks - play the chord audio
  // useCallback ensures stable reference to prevent child re-renders
  // First checks if the chord has a custom definition, otherwise falls back to tonal.js
  const handleChordClick = useCallback(
    (chordName: string) => {
      // Normalize Unicode symbols (♯/♭) to ASCII (#/b) for comparison with definitions
      const normalizedName = normalizeChordSymbol(chordName);

      // Look for a custom definition first (handles chords tonal.js doesn't know)
      const definition = chordDefinitions.find(
        (def) => def.name === normalizedName,
      );

      if (definition) {
        playChordFromDefinition(definition);
      } else {
        playChord(chordName);
      }
    },
    [chordDefinitions],
  );

  // Handle text selection for comment anchoring
  useEffect(() => {
    if (!onAddCommentAtSelection) return;

    const checkSelection = () => {
      try {
        const sel = window.getSelection();
        const container = containerRef.current;
        const selectedText = sel?.toString().trim();

        // Only show popup for meaningful selections (3+ chars, within our container)
        if (
          !sel ||
          sel.isCollapsed ||
          !container ||
          !selectedText ||
          selectedText.length < 3 ||
          sel.rangeCount === 0
        ) {
          setSelection(null);
          return;
        }

        const range = sel.getRangeAt(0);
        if (!container.contains(range.commonAncestorContainer)) {
          setSelection(null);
          return;
        }

        // Get position for the popup relative to the container
        const rect = range.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        setSelection({
          text: selectedText,
          position: {
            x: rect.left + rect.width / 2 - containerRect.left,
            y: rect.top - containerRect.top,
            right: rect.right - containerRect.left,
            centerY: rect.top + rect.height / 2 - containerRect.top,
          },
        });
      } catch {
        // Selection APIs can throw in edge cases (cross-origin iframes, etc.)
        setSelection(null);
      }
    };

    // Safari doesn't reliably fire selectionchange, so we also listen to mouseup/touchend
    const container = containerRef.current;

    document.addEventListener("selectionchange", checkSelection);
    container?.addEventListener("mouseup", checkSelection, { passive: true });
    container?.addEventListener("touchend", checkSelection, { passive: true });

    return () => {
      document.removeEventListener("selectionchange", checkSelection);
      container?.removeEventListener("mouseup", checkSelection);
      container?.removeEventListener("touchend", checkSelection);
    };
  }, [onAddCommentAtSelection]);

  // Handle adding comment at selection
  const handleAddComment = useCallback(() => {
    if (!selection || !onAddCommentAtSelection) return;

    // Create the anchor markup with proper escaping
    const anchorMarkup = createAnchorMarkup(selection.text);
    onAddCommentAtSelection(anchorMarkup);

    // Clear selection
    window.getSelection()?.removeAllRanges();
    setSelection(null);
  }, [selection, onAddCommentAtSelection]);

  // If not ChordPro, render as plain text
  if (!song) {
    return (
      <div
        className={`whitespace-pre-wrap font-mono text-sm leading-relaxed ${className}`}
      >
        {content || (
          <span className="italic text-text-muted">No notes yet</span>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative ${showChords ? "" : "hide-chords"} ${className}`.trim()}
    >
      {/* Selection popover */}
      {selection && onAddCommentAtSelection && (
        <SelectionPopover
          position={selection.position}
          onAddComment={handleAddComment}
          onDismiss={() => {
            window.getSelection()?.removeAllRanges();
            setSelection(null);
          }}
        />
      )}

      {/* Chord diagrams section */}
      {showDiagrams && showChords && chordDefinitions.length > 0 && (
        <div className="mb-6 flex flex-wrap items-end gap-4">
          {chordDefinitions.map((chord) => (
            <SVGuitarRenderer key={chord.name} chord={chord} width={140} />
          ))}
        </div>
      )}

      {/* Chord sheet content - rendered directly to React elements */}
      <ChordSheetContent
        song={song}
        onChordClick={handleChordClick}
        highlights={allHighlights}
        parsedFeedback={parsedFeedback}
        onInlineCommentClick={onInlineCommentClick}
      />
    </div>
  );
});
